import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { label: 'Home', path: '/homepage', icon: 'Home' },
    { label: 'Services', path: '/services', icon: 'Briefcase' },
    { label: 'About', path: '/about', icon: 'User' },
    { label: 'Gallery', path: '/gallery', icon: 'Image' },
    { label: 'Testimonials', path: '/testimonials', icon: 'MessageSquare' },
    { label: 'Contact', path: '/contact', icon: 'Phone' }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'Facebook', url: 'https://www.facebook.com/luxeallureevents/' },
    { name: 'Instagram', icon: 'Instagram', url: 'https://www.instagram.com/luxeallureevents/' },
    { name: 'Twitter', icon: 'Twitter', url: 'https://www.tiktok.com/discover/luxe-allure-events?lang=en' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isMobileMenuOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-40 transition-smooth ${
        isScrolled ? 'glassmorphic elevation-card' : 'bg-background/95'
      }`}>
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            
            <div className="flex-shrink-0">
              <button
                onClick={() => handleNavigation('/homepage')}
                className="flex items-center space-x-3 group transition-smooth"
              >
                <img 
                  src="/assets/logoo.png"
                  alt="Luxe & Allure Logo" 
                  className="h-10 w-auto group-hover:scale-105 transition-transform duration-300"
                />
               
              </button>
            </div>

            <nav className="hidden lg:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`font-body font-medium text-sm px-4 py-2 rounded-lg transition-smooth hover-scale ${
                    isActivePath(item.path)
                      ? 'bg-foreground text-background'
                      : 'text-foreground hover:text-primary hover:bg-muted'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-muted-foreground hover:text-primary transition-smooth hover-scale"
                    aria-label={social.name}
                  >
                    <Icon name={social.icon} size={18} />
                  </a>
                ))}
              </div>
              <div className="w-px h-6 bg-border mx-2"></div>
              <a
                href="tel:+254727937010"
                className="font-mono text-sm text-primary hover:text-accent transition-smooth"
              >
                (254) 727-9370
              </a>
            </div>

            <div className="lg:hidden flex items-center space-x-3">
              <a
                href="tel:+254727937010"
                className="font-mono text-sm text-primary"
              >
                <Icon name="Phone" size={20} />
              </a>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMobileMenu}
                className="text-foreground"
              >
                <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* --- DARK GLASSMORPHIC MOBILE MENU --- */}
      {isMobileMenuOpen && (
        <div 
          // This outer div creates the dark, heavily blurred background
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-lg animate-fade-in"
          onClick={toggleMobileMenu}
        >
          {/* This inner div is the "tablet" itself */}
          <div 
            className="w-full max-w-md p-6 rounded-2xl shadow-2xl dark-glassmorphic-tablet animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="space-y-4">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-smooth hover-scale ${
                    isActivePath(item.path)
                      ? 'bg-white text-black' // High contrast active link
                      : 'text-white hover:bg-white/10' // Text is now white
                  }`}
                >
                  <Icon name={item.icon} size={20} />
                  <span className="font-body font-medium">{item.label}</span>
                </button>
              ))}
            </nav>

            <div className="mt-8 pt-6 border-t border-white/20">
              <div className="flex items-center justify-center space-x-4 mb-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-white/10 rounded-lg text-white/80 hover:text-white hover:bg-white/20 transition-smooth hover-scale"
                    aria-label={social.name}
                  >
                    <Icon name={social.icon} size={20} />
                  </a>
                ))}
              </div>
              <div className="text-center">
                <a
                  href="tel:+254727937010"
                  className="inline-flex items-center space-x-2 font-mono text-white hover:text-white/80 transition-smooth"
                >
                  <Icon name="Phone" size={18} />
                  <span>+254727-937010</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;